import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { Row } from 'antd';
import { UserOutlined, SettingOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import WorkflowSimpleMappingConfigureDrawer from './WorkflowSimpleMappingConfigureDrawer';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import { updateData } from './workflowHelpers';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

const getUserFields = () => [
  { key: 'name', text: 'Name', type: 'text' },
  { key: 'email', text: 'Email', type: 'text' },
  { key: 'phoneNumber', text: 'Phone Number', type: 'text' },
  { key: 'wage', text: 'Wage', type: 'text' },
  { key: 'employeeId', text: 'Employee ID', type: 'text' },
  { key: 'projects', text: 'Project', type: 'dropdown - Projects' },
  { key: 'labels', text: 'Label', type: 'dropdown - Labels' },
  { key: 'roles', text: 'User Role', type: 'dropdown - Roles' },
  { key: 'divisions', text: 'Division', type: 'dropdown - Divisions' },
];

const requiredFields = new Set(['name', 'email']);
const HOVER_TEXT = 'This step can create a user based on the fields submitted.';
const DISABLED_TEXT = 'Add at least one Text Field to enable this step';
const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires <b>Name</b> and <b>Email</b> field mappings to be configured (via <b>Configure</b>).
      <br /><br />
      <b>Username will be auto-generated</b> from the Name field.
    </div>
  ),
};

export default function WorkflowCreateUserNode({
  isDisplay,
  setElements,
  setDataMap,
  name,
  sections = [],
  setDisableNext,
} = {}) {
  return function _({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const {
      fieldMappings: initialFieldMappings = {},
      sendWelcomeEmail: initialSendWelcomeEmail = true,
    } = data;

    const userCustomFields = useSelector((state) => state.users.customFieldTemplate);

    const [sendWelcomeEmail, setSendWelcomeEmail] = useState(initialSendWelcomeEmail);

    const title = `Create User${disabled ? ' - DISABLED' : ''}`;

    const [showDrawer, setShowDrawer] = useState(false);
    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);

    const openDrawer = useCallback(() => setShowDrawer(true), []);
    const closeDrawer = useCallback(() => setShowDrawer(false), []);

    useEffect(() => {
      if (!setDisableNext) return;

      const hasRequiredFields = fieldMappings?.name && fieldMappings?.email;
      setDisableNext((prev) => ({ ...prev, [id]: !hasRequiredFields }));
    }, [fieldMappings, setDisableNext, id]);

    const onUpdateData = useCallback((key, setterFunc) => (newValue) => {
      if (!setDataMap || !id) return;
      setterFunc(newValue);
      setDataMap(updateData(id, { [key]: newValue }));
    }, [setDataMap, id]);

    const relevantFields = useMemo(() => {
      const userNativeFields = getUserFields();

      const parsedUserCustomFields = [];

      userCustomFields?.sections?.forEach((section) => {
        const { fields = [], name: sectionName } = section ?? {};
        fields?.forEach((field) => {
          const {
            id: fieldId,
            configProps: {
              title: fieldTitle,
              dataType: fieldDataType,
            } = {},
            selectedType: fieldType,
          } = field ?? {};
          parsedUserCustomFields.push({
            ...field,
            text: `${sectionName} - ${fieldTitle}`,
            key: fieldId,
            dataType: fieldDataType,
            types: fieldType === 'attribute' ? ['text', 'attribute'] : [],
          });
        });
      });

      return userNativeFields.concat(parsedUserCustomFields);
    }, [userCustomFields]);

    return (
      <WorkflowActionNode
        id={id}
        title={title}
        type="createUser"
        Icon={UserOutlined}
        draggable={draggable}
        isDisplay={isDisplay || disabled}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : { width: 210 }}
        errorPopover={!fieldMappings?.name || !fieldMappings?.email ? ERROR_DETAILS : null}
      >
        {!draggable && (
          <div>
            <Row style={{ marginTop: 10 }}>
              <OnTraccrCheckbox
                style={{ marginTop: 5 }}
                onChange={onUpdateData('sendWelcomeEmail', setSendWelcomeEmail)}
                defaultChecked={initialSendWelcomeEmail}
                value={sendWelcomeEmail}
                label="Send Welcome Email"
                hoverNode={(
                  <div style={{ width: 300 }}>
                    Check this box to send a welcome email to the newly created user with their login credentials.
                    <br /><br />
                    Leave unchecked to create the user without sending an email.
                  </div>
                )}
              />
            </Row>
            <Row style={{ margin: '20px 0px' }}>
              <BorderlessButton
                title="Configure"
                style={{ margin: 5 }}
                iconNode={<SettingOutlined />}
                onClick={openDrawer}
              />
            </Row>
            <WorkflowSimpleMappingConfigureDrawer
              id={id}
              visible={showDrawer}
              onClose={closeDrawer}
              onSubmit={closeDrawer}
              sourceName={name}
              sourceSections={sections}
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onUpdateData('fieldMappings', setFieldMappings)}
              data={data}
              setDataMap={setDataMap}
              fields={relevantFields}
              requiredFields={requiredFields}
              targetHeader="Mapped Field"
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
            <WorkflowHandle type="source" position="bottom" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
    );
  };
}
